import React from "react"
import { Mail, ArrowLeft } from "lucide-react"
import SEO from "../components/SEO"
import { Link } from "react-router-dom"

export default function PrivacyPolicy() {
    // const currentDate = new Date().toLocaleDateString('en-US', {
    //     year: 'numeric',
    //     month: 'long',
    //     day: 'numeric'
    // })

    return (
        <div className="min-h-screen bg-gray-50">
            <SEO title="Privacy Policy" />
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="container mx-auto px-4 md:px-6 py-6">
                    <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Home
                    </Link>
                </div>
            </div>

            {/* Content */}
            <div className="container mx-auto px-4 md:px-6 py-12">
                <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm p-8 md:p-12">
                    <div className="mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
                        <p className="text-gray-600">Bousanm LLC</p>
                        <p className="text-sm text-gray-500 mt-2">Last updated: January 31, 2026</p>
                    </div>

                    <div className="prose prose-blue max-w-none">
                        {/* Section 1 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                            <p className="text-gray-700 leading-relaxed mb-4">
                                Bousanm LLC ("Bousanm", "we", "our", "us"), a company registered in Wyoming, USA, is committed to protecting the privacy of our clients and partners. This policy explains how we handle data across our core operations: Global Shipping & Logistics, E-commerce Infrastructure, and Software Development.
                            </p>
                        </section>

                        {/* Section 2 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
                            <p className="text-gray-700 leading-relaxed mb-4">
                                To facilitate international logistics and technology services, we collect:
                            </p>
                            <ul className="list-disc pl-6 space-y-4 text-gray-700">
                                <li>
                                    <span className="font-bold">Shipping Information:</span> Consignee name, delivery address, phone number, and package descriptions (invoices/values) for customs clearance.
                                </li>
                                <li>
                                    <span className="font-bold">Business & Contact Data:</span> Email, EIN/Tax ID, and professional information for corporate clients.
                                </li>
                                <li>
                                    <span className="font-bold">Compliance Data:</span> Identification documents required for international trade regulations and Anti-Money Laundering (AML) standards.
                                </li>
                                <li>
                                    <span className="font-bold">Technical Data:</span> IP addresses and tracking logs to monitor shipment status and API usage.
                                </li>
                            </ul>
                        </section>

                        {/* Section 3 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
                            <p className="text-gray-700 leading-relaxed mb-3">We use the collected data to:</p>
                            <ul className="list-disc pl-6 space-y-2 text-gray-700">
                                <li>Coordinate freight forwarding, air/sea cargo, and last-mile delivery.</li>
                                <li>Manage customs documentation and international trade compliance.</li>
                                <li>Provide real-time tracking updates for e-commerce orders.</li>
                                <li>Deliver custom software solutions and maintain secure API environments.</li>
                            </ul>
                        </section>

                        {/* Section 4 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Sharing & Third Parties</h2>
                            <p className="text-gray-700 leading-relaxed mb-4">We share information only with entities necessary to complete your requests:</p>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <h4 className="font-bold text-slate-900 mb-1 italic">Logistics Carriers</h4>
                                    <p className="text-slate-600 text-sm italic">International airlines, shipping lines, and local courier services (e.g., in Haiti, Chile, Brazil).</p>
                                </div>
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <h4 className="font-bold text-slate-900 mb-1 italic">Customs Authorities</h4>
                                    <p className="text-slate-600 text-sm italic">Government agencies required for the legal import/export of goods.</p>
                                </div>
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <h4 className="font-bold text-slate-900 mb-1 italic">Financial Partners</h4>
                                    <p className="text-slate-600 text-sm italic">Regulated institutions (Stripe, Mercury) to facilitate business-related payments and procurement tools.</p>
                                </div>
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <h4 className="font-bold text-slate-900 mb-1 italic">Technology Providers</h4>
                                    <p className="text-slate-600 text-sm italic">Encrypted cloud storage services for data security.</p>
                                </div>
                            </div>
                        </section>

                        {/* Section 5 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
                            <p className="text-gray-700 leading-relaxed">
                                Bousanm Technology implements rigorous technical and organizational measures to protect your data. All sensitive logistics and financial information is transmitted via secure, encrypted channels (SSL/TLS).
                            </p>
                        </section>

                        {/* Section 6 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
                            <p className="text-gray-700 leading-relaxed">
                                We retain logistics and transaction records for the duration required by US international trade laws and Wyoming state regulations, typically up to 7 years.
                            </p>
                        </section>

                        {/* Section 7 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Rights</h2>
                            <p className="text-gray-700 leading-relaxed">
                                You have the right to access, update, or request the deletion of your personal data. For logistics-related data, certain information must be retained for legal and customs compliance purposes.
                            </p>
                        </section>

                        {/* Section 8 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Governing Law</h2>
                            <p className="text-gray-700 leading-relaxed">
                                This Privacy Policy is governed by the laws of the State of Wyoming, USA.
                            </p>
                        </section>

                        {/* Section 9 */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Contact Us</h2>
                            <p className="text-gray-700 leading-relaxed mb-4">
                                For any privacy or compliance inquiries:
                            </p>
                            <div className="space-y-4">
                                <a href="mailto:support@bousanmtechnology.com" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-lg">
                                    <Mail className="w-5 h-5" />
                                    support@bousanmtechnology.com
                                </a>
                                <div className="text-gray-600">
                                    <p className="font-bold">Address:</p>
                                    <p>30 N Gould St Ste R, Sheridan, WY 82801 US</p>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    )
}
